import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import SEO from './components/SEO';
import { Toaster } from 'react-hot-toast';
import { ToastProvider } from './context/ToastContext';
import useGeolocationTracker from './hooks/useGeolocationTracker';
import LandingPage from './components/Landing/LandingPage';
import QuoteResultsPage from './components/Landing/QuoteResultsPage';
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";
import './App.css';

// Helper to catch ChunkLoadError and reload the page automatically
const lazyWithRetry = (componentImport) =>
  React.lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
    );
    try {
      const component = await componentImport();
      window.sessionStorage.setItem('page-has-been-force-refreshed', 'false');
      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
        window.location.reload();
      }
      throw error;
    }
  });

const Signup = lazyWithRetry(() => import('./components/Auth/Signup'));
const ForgotPassword = lazyWithRetry(() => import('./components/Auth/ForgotPassword'));
const VerifyEmail = lazyWithRetry(() => import('./components/Auth/VerifyEmail'));
const ResetPassword = lazyWithRetry(() => import('./components/Auth/ResetPassword'));
const ProtectedRoute = lazyWithRetry(() => import('./components/Auth/ProtectedRoute'));

const WaterTankerPage = lazyWithRetry(() => import('./components/Landing/WaterTankerPage'));
const GeneralHeavyTrucksPage = lazyWithRetry(() => import('./components/Landing/GeneralHeavyTrucksPage'));
const GeneralEquipmentRentalsPage = lazyWithRetry(() => import('./components/Landing/GeneralEquipmentRentalsPage'));
const GeneralConstructionVehiclesPage = lazyWithRetry(() => import('./components/Landing/GeneralConstructionVehiclesPage'));
const EquipmentCategoryPage = lazyWithRetry(() => import('./components/Landing/EquipmentCategoryPage'));
const ClientLayout = lazyWithRetry(() => import('./components/Client/ClientLayout'));
const ClientDashboard = lazyWithRetry(() => import('./components/Client/ClientDashboard'));
const FleetPartnerRegister = lazyWithRetry(() => import('./components/FleetPartner/FleetPartnerRegister'));
const FleetPartnerDashboard = lazyWithRetry(() => import('./components/FleetPartner/FleetPartnerDashboard'));
const Login = lazyWithRetry(() => import('./components/Auth/Login'));

const TripStatusPortalPage = lazyWithRetry(() => import('./components/FleetPartner/modules/Shipments/components/TripStatusPortalPage'));

const AdminLogin = lazyWithRetry(() => import('./components/Admin/AdminLogin'));
const AdminResetPassword = lazyWithRetry(() => import('./components/Admin/AdminResetPassword'));
const AdminProtectedRoute = lazyWithRetry(() => import('./components/Admin/AdminProtectedRoute'));
const AdminLayout = lazyWithRetry(() => import('./components/Admin/AdminLayout'));
const DashboardOverview = lazyWithRetry(() => import('./components/Admin/DashboardOverview'));
const AdminOperations = lazyWithRetry(() => import('./components/Admin/AdminOperations'));
const AdminFleets = lazyWithRetry(() => import('./components/Admin/AdminFleets'));
const AdminCustomers = lazyWithRetry(() => import('./components/Admin/AdminCustomers'));
const AdminSpyBooking = lazyWithRetry(() => import('./components/Admin/AdminSpyBooking'));
const AdminPricing = lazyWithRetry(() => import('./components/Admin/AdminPricing'));
const AdminPayments = lazyWithRetry(() => import('./components/Admin/AdminPayments'));
const AdminTracking = lazyWithRetry(() => import('./components/Admin/AdminTracking'));
const AdminAnalytics = lazyWithRetry(() => import('./components/Admin/AdminAnalytics'));
const AdminSupport = lazyWithRetry(() => import('./components/Admin/AdminSupport'));
const AdminSecurityUnlocks = lazyWithRetry(() => import('./components/Admin/AdminSecurityUnlocks'));
const AdminSystemAlerts = lazyWithRetry(() => import('./components/Admin/AdminSystemAlerts'));
const AdminSettings = lazyWithRetry(() => import('./components/Admin/AdminSettings'));

// Admin Enterprise Observability
const SystemHealth = lazyWithRetry(() => import('./components/Admin/modules/Observability/SystemHealth'));
const PerformanceDashboard = lazyWithRetry(() => import('./components/Admin/modules/Observability/PerformanceDashboard'));
const DisasterRecovery = lazyWithRetry(() => import('./components/Admin/modules/Observability/DisasterRecovery'));
const ApiVersion = lazyWithRetry(() => import('./components/Admin/modules/Observability/ApiVersion'));

// Admin Enterprise Auditing & Security
const AuditLogs = lazyWithRetry(() => import('./components/Admin/modules/Auditing/AuditLogs'));
const RecycleBin = lazyWithRetry(() => import('./components/Admin/modules/Auditing/RecycleBin'));
const FraudCenter = lazyWithRetry(() => import('./components/Admin/modules/Security/FraudCenter'));
const RateLimitDashboard = lazyWithRetry(() => import('./components/Admin/modules/Security/RateLimitDashboard'));
const ExpirationCenter = lazyWithRetry(() => import('./components/Admin/modules/Security/ExpirationCenter'));

// Admin Enterprise Background Jobs
const QueueDashboard = lazyWithRetry(() => import('./components/Admin/modules/Background/QueueDashboard'));
const EventBusMonitor = lazyWithRetry(() => import('./components/Admin/modules/Background/EventBusMonitor'));

const BookingFormsSpot = lazyWithRetry(() => import('./components/Client/BookingForms/SpotBookingForm'));
const BookingFormsRental = lazyWithRetry(() => import('./components/Client/BookingForms/RentalBookingForm'));
const BookingMapPage = lazyWithRetry(() => import('./components/Map/BookingMapPage'));
const MyBookings = lazyWithRetry(() => import('./components/Client/MenuPages/MyBookings'));
const MyShipments = lazyWithRetry(() => import('./components/Client/MenuPages/MyShipments'));
const PremiumPass = lazyWithRetry(() => import('./components/Client/MenuPages/PremiumPass'));
const ClientLiveMap = lazyWithRetry(() => import('./components/Client/MenuPages/ClientLiveMap'));
const TrackLiveShipment = lazyWithRetry(() => import('./components/Client/MenuPages/TrackLiveShipment'));
const ClientInbox = lazyWithRetry(() => import('./components/Client/MenuPages/ClientInbox'));
const ClientDocuments = lazyWithRetry(() => import('./components/Client/MenuPages/ClientDocuments'));
const ClientAccount = lazyWithRetry(() => import('./components/Client/MenuPages/ClientAccount'));
const SupportChat = lazyWithRetry(() => import('./components/Client/MenuPages/SupportChat'));
const ClientPayments = lazyWithRetry(() => import('./components/Client/MenuPages/ClientPayments'));

const FavoritePartners = lazyWithRetry(() => import('./components/Client/MenuPages/FavoritePartners'));
const MyBusiness = lazyWithRetry(() => import('./components/Client/MenuPages/MyBusiness'));
const CompanyProfile = lazyWithRetry(() => import('./components/Client/CompanyProfile'));
const AboutUs = lazyWithRetry(() => import('./components/About/AboutUs'));
const ContactUs = lazyWithRetry(() => import('./components/Support/ContactUs'));
const Help = lazyWithRetry(() => import('./components/Support/Help'));
const CareersPage = lazyWithRetry(() => import('./components/Landing/CareersPage'));
const BlogPage = lazyWithRetry(() => import('./components/Landing/BlogPage'));
const BlogPostPage = lazyWithRetry(() => import('./components/Landing/BlogPostPage'));
const PrivacyPolicyPage = lazyWithRetry(() => import('./components/Landing/PrivacyPolicyPage'));
const TermsOfServicePage = lazyWithRetry(() => import('./components/Landing/TermsOfServicePage'));
const InsuranceFAQsPage = lazyWithRetry(() => import('./components/Landing/InsuranceFAQsPage'));
const RequestQuotePage = lazyWithRetry(() => import('./components/Client/MenuPages/RequestQuotePage'));
const ContractorsBookingPage = lazyWithRetry(() => import('./components/Client/MenuPages/ContractorsBookingPage'));
const BookNowPage = lazyWithRetry(() => import('./components/Client/MenuPages/BookNowPage'));


function App() {
  const { requestPermission } = useGeolocationTracker();

  useEffect(() => {
    // Request permission on app load for better service/tracking
    requestPermission()
      .then(() => {})
      .catch(() => { /* silent fallback */ });

    // Global Image Preloader for the Entire Platform
    // Preload heavy background images silently after initial load to eliminate flashing when switching pages
    const preloadImages = () => {
      const globalBgImages = [
        '/primaryprofilebg.webp',
        '/shipmentsbg.webp',
        '/booking-bg.webp',
        '/rental-bg.webp',
        '/generalheavybg.webp',
        '/generalequipment.webp',
        '/generalconstructionbg.webp'
      ];
      globalBgImages.forEach(src => {
        const img = new Image();
        img.src = src;
      });
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(preloadImages, { timeout: 2000 });
    } else {
      setTimeout(preloadImages, 3000);
    }

    // Global listener for Enter key to focus the next field
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        const target = e.target;
        const tag = target.tagName.toLowerCase();
        
        // Ignore textareas (allow new lines) and buttons
        if (tag === 'textarea' || tag === 'button' || target.type === 'submit') {
          return;
        }

        if (tag === 'input' || tag === 'select') {
          const form = target.form;
          let focusableElements = [];
          
          if (form) {
             focusableElements = Array.from(form.querySelectorAll('input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'));
          } else {
             // Fallback if not inside a form
             focusableElements = Array.from(document.querySelectorAll('input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'));
          }

          // Filter out elements that are effectively hidden
          focusableElements = focusableElements.filter(el => {
             return el.type !== 'hidden' && el.tabIndex !== -1 && el.offsetParent !== null;
          });

          const index = focusableElements.indexOf(target);
          if (index > -1 && index < focusableElements.length - 1) {
            e.preventDefault(); // Prevent accidental form submission
            focusableElements[index + 1].focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [requestPermission]);

  return (
    <ToastProvider>
      <Router>
        <div className="App">
          <Toaster position="top-center" reverseOrder={false} />
          <SpeedInsights />
          <Analytics />
          <React.Suspense fallback={<div className="flex h-screen w-screen items-center justify-center bg-black"><div className="w-11 h-11 border-[3.5px] border-[#F9B101]/20 border-t-[#F9B101] rounded-full animate-spin"></div></div>}>
            <main id="main-content">
              <Routes>
            <Route path="/" element={
              window.location.hostname.startsWith('admin') ? <Navigate to="/admin" replace /> :
              <><SEO 
                title="KaroTrukk | India's First Cab-Style Hail Booking Platform for JCBs & Equipment Machineries" 
                description="India's first cab-style hail booking platform for JCBs and equipment machineries in few steps." 
                canonical="https://karotrukk.com/" 
                keywords="KaroTrukk, hail booking, cab-style hail booking, JCB hail booking, construction machinery hail booking, heavy equipment hail booking India, Construction machinery booking platform India, JCB booking online, Construction equipment rental booking, Excavator booking, Heavy equipment booking platform, Construction machinery near me, Truck and tanker booking, Fleet partner platform, Cab-style machinery booking, India first cab style machinery booking, book JCB online, equipment machineries booking platform, fleet partner network India" 
              /><LandingPage /></>
            } />
            <Route path="/quote-results" element={
              <><SEO title="Price Quotes | KaroTrukk" description="Get instant price estimates for heavy trucks and equipment." /><QuoteResultsPage /></>
            } />
            <Route path="/water-tanker" element={<><SEO title="Water Tanker Booking | KaroTrukk" description="Book water tankers for residential, commercial, or construction needs." /><WaterTankerPage /></>} />
            <Route path="/general-heavy-trucks" element={<><SEO title="Heavy Trucks Booking | KaroTrukk" description="Rent general heavy trucks for large scale transport and logistics." /><GeneralHeavyTrucksPage /></>} />
            <Route path="/general-equipment-rentals" element={<><SEO title="Equipment Rentals | KaroTrukk" description="Rent reliable construction and industrial equipment for your projects." /><GeneralEquipmentRentalsPage /></>} />
            <Route path="/general-construction-vehicles" element={<><SEO title="Construction Vehicles | KaroTrukk" description="Book high-quality construction vehicles including excavators and cranes." /><GeneralConstructionVehiclesPage /></>} />
            <Route path="/equipment/:category" element={<><SEO title="Equipment Categories | KaroTrukk" description="Browse our extensive fleet of vehicles and equipment by category." /><EquipmentCategoryPage /></>} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<><SEO title="Client Login | KaroTrukk" description="Log in to your KaroTrukk account to book JCBs and equipment machineries in few steps." canonical="https://karotrukk.com/login" /><Login defaultRole="client" /></>} />
            <Route path="/client/login" element={<><SEO title="Client Login | KaroTrukk" description="Log in to your KaroTrukk account to book JCBs and equipment machineries in few steps." canonical="https://karotrukk.com/login" /><Login defaultRole="client" /></>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/customer/forgot-password" element={<ForgotPassword defaultRole="client" />} />
            <Route path="/vehicle-owner/forgot-password" element={<ForgotPassword defaultRole="fleet-partner" />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/customer/reset-password/:token" element={<ResetPassword defaultRole="client" />} />
            <Route path="/vehicle-owner/reset-password/:token" element={<ResetPassword defaultRole="fleet-partner" />} />
            <Route path="/fleet-partner/register" element={<><SEO title="Register as a Fleet Partner | KaroTrukk" description="Join KaroTrukk as a fleet partner and grow your transport business." /><FleetPartnerRegister /></>} />
            <Route path="/fleet-partner/dashboard" element={
              <ProtectedRoute allowedRoles={['fleet-partner', 'vehicle-owner']}>
                <FleetPartnerDashboard />
              </ProtectedRoute>
            } />



            {/* Admin Routes (10 Pure Monochrome Sections) */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/reset-password/:token" element={<AdminResetPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin" element={<AdminProtectedRoute />}>
              <Route element={<AdminLayout />}>
                {/* 1. Dashboard */}
                <Route index element={<DashboardOverview />} />
                <Route path="dashboard" element={<DashboardOverview />} />
                
                {/* 2. Operations */}
                <Route path="operations" element={<AdminOperations />} />
                <Route path="operations/bookings" element={<AdminOperations />} />
                <Route path="operations/requests" element={<AdminOperations />} />
                <Route path="operations/broadcasts" element={<AdminOperations />} />
                <Route path="operations/cancellations" element={<AdminOperations />} />

                {/* 3. Fleet */}
                <Route path="fleet" element={<AdminFleets />} />
                <Route path="fleet/partners" element={<AdminFleets />} />
                <Route path="fleet/vehicles" element={<AdminFleets />} />
                <Route path="fleet/drivers" element={<AdminFleets />} />

                {/* 4. Clients */}
                <Route path="clients" element={<AdminCustomers />} />
                <Route path="customers" element={<AdminCustomers />} />
                <Route path="spy-booking/:bookingId" element={<AdminSpyBooking />} />

                {/* 5. Pricing */}
                <Route path="pricing" element={<AdminPricing />} />

                {/* 6. Payments */}
                <Route path="payments" element={<AdminPayments />} />
                <Route path="payments/transactions" element={<AdminPayments />} />
                <Route path="payments/refunds" element={<AdminPayments />} />
                <Route path="payments/settlements" element={<AdminPayments />} />

                {/* 7. Tracking */}
                <Route path="tracking" element={<AdminTracking />} />

                {/* 8. Analytics */}
                <Route path="analytics" element={<AdminAnalytics />} />

                {/* 9. Support */}
                <Route path="support" element={<AdminSupport />} />

                {/* 10. Security & Unlocks */}
                <Route path="security-unlocks" element={<AdminSecurityUnlocks />} />

                {/* 10B. System Alerts & Dual-Approval (Super Admin Only) */}
                <Route path="system-alerts" element={<AdminSystemAlerts />} />

                {/* 11. Settings */}
                <Route path="settings" element={<AdminSettings />} />
              </Route>
            </Route>

            {/* Client & Partner Routes */}
            <Route path="/vehicle-owner/login" element={<><SEO title="Fleet Partner Login | KaroTrukk" description="Log in to access your fleet partner dashboard, manage vehicles, and accept machinery bookings." canonical="https://karotrukk.com/vehicle-owner/login" /><Login defaultRole="fleet-partner" /></>} />
            <Route path="/fleet-partner/login" element={<><SEO title="Fleet Partner Login | KaroTrukk" description="Log in to access your fleet partner dashboard, manage vehicles, and accept machinery bookings." canonical="https://karotrukk.com/vehicle-owner/login" /><Login defaultRole="fleet-partner" /></>} />
            <Route path="/vehicle-owner/signup" element={<FleetPartnerRegister />} />
            
            <Route path="/customer/login" element={<><SEO title="Client Login | KaroTrukk" description="Log in to your KaroTrukk customer account to book construction machinery, JCBs, excavators, and commercial trucks." canonical="https://karotrukk.com/login" /><Login defaultRole="client" /></>} />
            <Route path="/customer/signup" element={<Signup />} />

            <Route element={
              <ProtectedRoute allowedRoles={['client', 'customer']}>
                <ClientLayout />
              </ProtectedRoute>
            }>
              <Route path="/client/dashboard" element={<ClientDashboard />} />
              <Route path="/customer/dashboard" element={<ClientDashboard />} />
              <Route path="/business/dashboard" element={<ClientDashboard />} />
              <Route path="/client/booknow" element={<BookNowPage />} />
              <Route path="/client/contractors" element={<ContractorsBookingPage />} />
              <Route path="/client/request-quote" element={<RequestQuotePage />} />
              <Route path="/client/bookings" element={<MyBookings />} />
              <Route path="/client/shipments" element={<MyShipments />} />
              <Route path="/client/tracking" element={<ClientLiveMap />} />
              <Route path="/client/track-booking" element={<TrackLiveShipment />} />
              <Route path="/client/track-booking/:requestId" element={<TrackLiveShipment />} />
            </Route>

            <Route path="/client/book/rental" element={<BookingFormsRental />} />
            <Route path="/client/book/spot" element={<BookingFormsSpot />} />
            <Route path="/booking/:id/map" element={<BookingMapPage />} />
            <Route path="/client/premium" element={<PremiumPass />} />
            <Route path="/client/inbox" element={<ClientInbox />} />
            <Route path="/client/documents" element={<ClientDocuments />} />
            <Route path="/client/account" element={<ClientAccount />} />
            <Route path="/client/support" element={<SupportChat />} />
            <Route path="/client/payments" element={<ClientPayments />} />

            <Route path="/client/favorite-partners" element={<FavoritePartners />} />
            <Route path="/client/my-business" element={<MyBusiness />} />
            <Route path="/client/company/:id" element={<CompanyProfile />} />
            <Route path="/tripstatusportal/:shipmentId" element={<TripStatusPortalPage />} />
            <Route path="/about" element={<><SEO title="About Us | KaroTrukk" description="Learn more about KaroTrukk and our mission to revolutionize transport and equipment booking." /><AboutUs /></>} />
            <Route path="/contact" element={<><SEO title="Contact KaroTrukk" description="Get in touch with the KaroTrukk support team for any inquiries or assistance." /><ContactUs /></>} />
            <Route path="/help" element={<><SEO title="Help Center | KaroTrukk" description="Find answers to frequently asked questions and get support." /><Help /></>} />
            <Route path="/careers" element={<><SEO title="Careers at KaroTrukk" description="Join our team and help build the future of logistics and transport." /><CareersPage /></>} />
            <Route path="/blog" element={<><SEO title="KaroTrukk Blog" description="Read the latest news, updates, and industry insights from KaroTrukk." /><BlogPage /></>} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/privacy" element={<><SEO title="Privacy Policy | KaroTrukk" description="Read our privacy policy to understand how we protect and use your data." /><PrivacyPolicyPage /></>} />
            <Route path="/terms" element={<><SEO title="Terms of Service | KaroTrukk" description="Read the terms and conditions for using the KaroTrukk platform." /><TermsOfServicePage /></>} />
            <Route path="/insurance" element={<><SEO title="Insurance FAQs | KaroTrukk" description="Learn about insurance coverage and policies for bookings on KaroTrukk." /><InsuranceFAQsPage /></>} />
          </Routes>
            </main>
            </React.Suspense>
        </div>
      </Router>
    </ToastProvider>
  );
}

export default App;

